<template>
  <div>
    <b-row class="mb-3 ml-2">
      <span class="title_detalle">{{ title }}</span>
    </b-row>

    <b-row>
      <b-col>
        <b-table
          v-if="detalles"
          :fields="fields"
          :items="detalles"
          :empty-text="$t('label.noData')"
          hover
          ref="tableDetalleTransferencia"
          show-empty
          small
          borderless
        >
          <template #cell(actions-left)="row">
            <div class="table-button-container d-flex">
              <b-icon
                :id="`info-detalle-${tipo}-${row.item.id}`"
                class="icon-details ml-3 my-auto cursor-pointer"
                icon="exclamation-circle"
                :alt="$t('label.informacion')"
              />
              <b-tooltip
                :target="`info-detalle-${tipo}-${row.item.id}`"
                custom-class="table-cell-tooltip"
                placement="bottom"
              >
                <template
                  v-for="(detalleTransferencia, index) in row.item.soVDetalleTransferencias"
                >
                  <div :key="`detalle-${detalleTransferencia.id}`">
                    <hr v-if="index" class="m-0" />

                    <div class="tooltip-inner-content-diff">
                      <div class="title-info">
                        {{ $t("label.denominacion") }}:
                        {{
                          detalleTransferencia.soCalidadDenominacion.denominacion.codDenominacion
                        }}
                        {{ detalleTransferencia.soCalidadDenominacion.desCalidad }}
                      </div>
                      <hr v-if="row.item.soVDetalleTransferencias.length === 1" class="m-0 pt-2" />
                      <div>
                        {{ $t("label.demandado") }}:
                        {{ filterNumber(detalleTransferencia.cantidadDemandada, 2) }}
                      </div>
                      <div>
                        {{ $t("label.transferido") }}:
                        {{ filterNumber(detalleTransferencia.cantidadTransferida, 2) }}
                      </div>
                    </div>
                  </div>
                </template>
              </b-tooltip>
              <b-icon
                v-if="row.item.estado === 'PENDIENTE'"
                :id="`cancel-${tipo}-${row.index}`"
                class="ml-3 my-auto cursor-pointer"
                icon="x-lg"
                v-b-tooltip.hover.d300="
                  tipo === 'OFERTA' ? $t('label.rechazarDemanda') : $t('label.cancelar')
                "
                :alt="tipo === 'OFERTA' ? $t('label.rechazarDemanda') : $t('label.cancelar')"
                @click="
                  tipo === 'OFERTA' ? rechazarDemanda(row.item.id) : cancelarDemanda(row.item.id)
                "
              />
              <b-icon
                v-if="row.item.estado === 'PENDIENTE' && tipo === 'OFERTA'"
                :id="`aceptar-${tipo}-${row.index}`"
                class="icon-details ml-3 my-auto cursor-pointer"
                icon="check2"
                height="20"
                width="20"
                v-b-tooltip.hover.d300="$t('action.aceptar')"
                :alt="$t('action.aceptar')"
                @click="aceptarDemanda(row.item)"
              />
            </div>
          </template>
          <template #cell(coste)="row">
            {{ $t(`label.coste.${row.value.codParametro}`) }}
          </template>
        </b-table>
      </b-col>
    </b-row>
  </div>
</template>

<script lang="ts">
import VueFilters from "@/vue-filters";
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { Action } from "vuex-class";
import { IField, ITransferencia, IOfertaDemanda } from "@/entities";

@Component({
  name: "detalle-transferencia",
  mixins: [VueFilters],
})
export default class DetalleTransferencia extends Vue {
  @Prop({ required: true }) ofertaDemanda!: IOfertaDemanda;
  @Prop({ required: true }) tipo!: "OFERTA" | "DEMANDA";
  @Action("fetchByOferta") fetchByOferta!: (id: number) => Promise<ITransferencia[] | undefined>;
  @Action("fetchByDemanda") fetchByDemanda!: (id: number) => Promise<ITransferencia[] | undefined>;

  @Action("setLoading") setLoading!: (loading: boolean) => void;

  detalles: ITransferencia[] = [];

  get title(): string {
    return this.tipo === "OFERTA"
      ? this.$t("label.entidadesOferta")
      : this.$t("label.entidadesDemanda");
  }

  get fields(): IField[] {
    const columns = [
      {
        key: "actions-left",
        label: this.$t("label.acciones"),
        sortable: false,
        thClass: "table-header ml-2",
        tdClass: "table-cell-first actions",
        visible: true,
        class: "table-cell",
        aux: true,
      },
      {
        key: "soVOferta.clientexdel.cliente.desCliente",
        label: this.$t("label.cliente"),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        visible: true,
        class: "table-cell",
        aux: false,
      },
      {
        key: "soVDemanda.clientexdel.cliente.desCliente",
        label: this.$t("label.cliente"),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        visible: true,
        class: "table-cell",
        aux: false,
      },
      {
        key: "cantidadDemandada",
        label: this.$t("label.cantidad"),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        visible: true,
        class: "table-cell",
        formatter: (value: number, _key: string, item: ITransferencia): string => {
          if (item.estado === "ACEPTADA") return this.filterNumber(item.cantidadTransferida, 2);
          else return this.filterNumber(value, 2);
        },
        aux: false,
      },
      {
        key: "soVOferta.coste.desParametro",
        label: this.$t("label.coste"),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        visible: true,
        class: "table-cell",
        aux: false,
      },
      {
        key: "estado",
        label: this.$t("label.estado"),
        sortable: true,
        thClass: "table-header",
        tdClass: (value: string): string => "table-cell-middle " + value.toLowerCase(),
        visible: true,
        class: "table-cell",
        aux: false,
      },
      {
        key: "fecCrear",
        label: this.$t("label.creacion"),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        visible: true,
        class: "table-cell",
        formatter: (value: string): string => this.filterDateTime(value),
        aux: false,
      },
      {
        key: "fecExpirar",
        label: this.$t("label.expiracion"),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        visible: true,
        class: "table-cell",
        formatter: (value: string): string => this.filterDateTime(value),
        aux: false,
      },
    ];

    if (this.tipo === "DEMANDA") {
      return columns.filter(function (value) {
        return (
          value.key != "checkParcial" && value.key != "soVDemanda.clientexdel.cliente.desCliente"
        );
      });
    } else if (this.tipo === "OFERTA") {
      return columns.filter(function (value) {
        return (
          value.key != "soVOferta.clientexdel.cliente.desCliente" &&
          value.key != "soVOferta.fecExpirar"
        );
      });
    }
    return columns;
  }
  constructor() {
    super();
  }
  mounted(): void {
    this.fetch();
  }

  rechazarDemanda(id: number): void {
    this.$emit("rechazarDemanda", id);
  }
  aceptarDemanda(transferencia: ITransferencia): void {
    this.$emit("aceptarDemanda", transferencia);
  }
  cancelarDemanda(id: number): void {
    this.$emit("cancelarDemandaPrivada", id);
  }

  async fetch(): Promise<void> {
    if (this.tipo === "OFERTA") {
      const response = await this.fetchByOferta(this.ofertaDemanda.id);
      if (response) {
        this.detalles = response;
      }
    } else {
      const response = await this.fetchByDemanda(this.ofertaDemanda.id);
      if (response) {
        this.detalles = response;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.title_detalle {
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.14;
  letter-spacing: normal;
  color: var(--secundario-prosegur-claro);
  margin-top: 10px;
}
</style>
